/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { PlanContextHints } from "@/hooks/useSolomonPlanner";

export type SolomonHintType = "hint" | "insight" | "workflow" | "alert";
export type SolomonHintPriority = "low" | "medium" | "high";

export interface SolomonAction {
  label: string;
  action: string;
  variant?: "default" | "primary" | "secondary" | "destructive";
  /** Optional href for navigation actions */
  href?: string;
}

export interface SolomonHint {
  id: string;
  message: string;
  context: string;
  timestamp?: number;
  /** Type of hint for styling/categorization */
  type?: SolomonHintType;
  /** Priority level for ordering */
  priority?: SolomonHintPriority;
  /** Icon name to display */
  icon?: string;
  /** Simple link (legacy support) */
  link?: {
    label: string;
    href: string;
  };
  /** Action buttons for interactive hints */
  actions?: SolomonAction[];
  /** Metadata for analytics and pattern tracking */
  metadata?: Record<string, unknown>;
}

export type SolomonActionHandler = (action: string, hintId: string, metadata?: Record<string, unknown>) => void;

interface SolomonContextValue {
  hints: SolomonHint[];
  unreadCount: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  showHint: (hint: SolomonHint) => void;
  dismissHint: (id: string) => void;
  markAllRead: () => void;
  isDismissed: (id: string) => boolean;
  clearAllHints: () => void;
  /** Execute an action from a hint */
  executeAction: SolomonActionHandler;
  /** Register a handler for custom actions */
  registerActionHandler: (handler: SolomonActionHandler) => () => void;
  /**
   * Page-level focus hints surfaced to the action-engine planner.
   * Pages (PersonProfile, GroupDetail, etc.) push the focused
   * entity here via `useSolomonFocus` so the planner can resolve
   * "this person" / "this group" without the user typing IDs.
   * `null` when no page has registered focus.
   */
  focusContext: PlanContextHints | null;
  /**
   * Replace the focus context. `null` clears it. Typically called
   * via `useSolomonFocus(hints)` which manages registration +
   * cleanup as a side effect. Focus is *singleton* — calling this
   * twice without an unmount overwrites the prior context.
   */
  setFocusContext: (next: PlanContextHints | null) => void;
}

const SolomonContext = createContext<SolomonContextValue | null>(null);

const DISMISSED_KEY = "solomon-dismissed-hints";
const READ_KEY = "solomon-read-hints";

export function SolomonProvider({ children }: { children: React.ReactNode }) {
  const [hints, setHints] = useState<SolomonHint[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [focusContext, setFocusContextState] = useState<PlanContextHints | null>(null);
  const [actionHandlers, setActionHandlers] = useState<Set<SolomonActionHandler>>(() => new Set());
  const [readHints, setReadHints] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(READ_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [dismissedHints, setDismissedHints] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(DISMISSED_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Persist dismissed hints
  useEffect(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissedHints]));
    } catch {
      // Ignore storage errors
    }
  }, [dismissedHints]);

  // Persist read hints
  useEffect(() => {
    try {
      localStorage.setItem(READ_KEY, JSON.stringify([...readHints]));
    } catch {
      // Ignore storage errors
    }
  }, [readHints]);

  // Calculate unread count
  const unreadCount = hints.filter(h => !readHints.has(h.id)).length;

  // Mark all as read when panel is opened
  useEffect(() => {
    if (isOpen && hints.length > 0) {
      setReadHints((prev) => {
        const next = new Set(prev);
        let changed = false;
        hints.forEach((h) => {
          if (!next.has(h.id)) {
            next.add(h.id);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [isOpen, hints]);

  const isDismissed = useCallback(
    (id: string) => dismissedHints.has(id),
    [dismissedHints]
  );

  const showHint = useCallback(
    (hint: SolomonHint) => {
      if (!isDismissed(hint.id)) {
        setHints((prev) => {
          // Don't add duplicate hints
          if (prev.some(h => h.id === hint.id)) return prev;
          return [...prev, { ...hint, timestamp: Date.now() }];
        });
      }
    },
    [isDismissed]
  );

  const dismissHint = useCallback((id: string) => {
    setDismissedHints((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setHints((prev) => (prev.some((h) => h.id === id) ? prev.filter((h) => h.id !== id) : prev));
  }, []);

  const markAllRead = useCallback(() => {
    setReadHints((prev) => {
      const next = new Set(prev);
      let changed = false;

      hints.forEach((hint) => {
        if (!next.has(hint.id)) {
          next.add(hint.id);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [hints]);

  const clearAllHints = useCallback(() => {
    setDismissedHints((prev) => {
      const next = new Set(prev);
      let changed = false;

      hints.forEach((hint) => {
        if (!next.has(hint.id)) {
          next.add(hint.id);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
    setHints((prev) => (prev.length > 0 ? [] : prev));
  }, [hints]);

  const executeAction = useCallback<SolomonActionHandler>(
    (action, hintId, metadata) => {
      // Find the hint to get its metadata
      const hint = hints.find(h => h.id === hintId);
      const baseMetadata = { ...hint?.metadata, ...(metadata ?? {}) };
      const whyThisHappened =
        baseMetadata.whyThisHappened ?? {
          hintId: hint?.id ?? hintId,
          hintType: hint?.type,
          context: hint?.context,
          message: hint?.message,
          priority: hint?.priority,
        };
      const combinedMetadata = {
        ...baseMetadata,
        whyThisHappened,
      };

      // Call all registered handlers
      actionHandlers.forEach(handler => {
        try {
          handler(action, hintId, combinedMetadata);
        } catch (error) {
          console.error("Solomon action handler error:", error);
        }
      });

      // Auto-dismiss hint after action (unless it's a dismiss action itself)
      if (action !== "dismiss") {
        dismissHint(hintId);
      }
    },
    [actionHandlers, hints, dismissHint]
  );

  const setFocusContext = useCallback((next: PlanContextHints | null) => {
    setFocusContextState((prev) => {
      // Cheap equality — avoid re-renders when a page's effect re-runs
      // with the same focus object. JSON.stringify is total over
      // PlanContextHints (flat record of optional strings) so any
      // new fields added to the type are compared automatically;
      // no field-by-field maintenance required.
      if (prev === null && next === null) return prev;
      if (
        prev !== null &&
        next !== null &&
        JSON.stringify(prev) === JSON.stringify(next)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const registerActionHandler = useCallback(
    (handler: SolomonActionHandler) => {
      setActionHandlers((prev) => {
        if (prev.has(handler)) return prev;
        const next = new Set(prev);
        next.add(handler);
        return next;
      });
      // Return unsubscribe function
      return () => {
        setActionHandlers(prev => {
          const next = new Set(prev);
          if (!next.has(handler)) {
            return prev;
          }
          next.delete(handler);
          return next;
        });
      };
    },
    []
  );

  return (
    <SolomonContext.Provider
      value={{
        hints,
        unreadCount,
        isOpen,
        setIsOpen,
        showHint,
        dismissHint,
        markAllRead,
        isDismissed,
        clearAllHints,
        executeAction,
        registerActionHandler,
        focusContext,
        setFocusContext,
      }}
    >
      {children}
    </SolomonContext.Provider>
  );
}

export function useSolomonContext() {
  const context = useContext(SolomonContext);
  if (!context) {
    throw new Error("useSolomonContext must be used within SolomonProvider");
  }
  return context;
}